import React, { useEffect, useState } from 'react'
import { lookupOrg, updatePreferences } from '../api'
import EinPrompt from './EinPrompt'
import OrgIdentityConfirm from './OrgIdentityConfirm'

/**
 * The post-confirmation home for the #62 EIN resolver
 * ([#65](https://github.com/Pat474/homelab-kb/issues/65)).
 *
 * `GET /api/org-lookup` requires a signed-in client with a CONFIRMED email
 * ([ADR-0018](docs/adr/0018-org-lookup-requires-a-confirmed-account.md)), which
 * a signup wizard by definition does not have — so the resolver left step 0 of
 * onboarding and had no caller at all until this component. It now runs from
 * the dashboard, where the client is already authenticated and (usually)
 * confirmed.
 *
 * ## Why the dashboard card rather than a /setup route
 *
 * `EinPrompt` already occupies the empty co-funder space and asks for a typed
 * EIN. Replacing it is strictly less new surface than a new route, and it keeps
 * ADR-0012's rule intact — ask at the point where it unlocks something visible.
 * The payoff is not described here, it is *rendered* here: confirming writes
 * `org_ein`, the parent re-fetches, and the leads appear in the rectangle the
 * question was asked in.
 *
 * ## Offer, then ask — never the other way round
 *
 * The lookup fires on mount rather than behind a button, because the entire
 * thesis of #62 is that we can OFFER the EIN instead of asking for it: measured
 * 90.2% correct on the organizations our data carries, ~59% end to end. A button
 * saying "look me up" would put the cheapest, highest-yield step behind a click
 * for no reason. It is one indexed query (prod median 0.7 ms) and the route's
 * rate limit is deliberately generous (40/min, ADR-0017).
 *
 * ## Every failure degrades to the typed field, silently
 *
 * `EinPrompt` is the fallback for **all** of: no match, an unconfirmed email
 * (403 — `get_current_client` admits an unconfirmed account, so this panel is
 * reachable by one), a missing state on the profile, and any network error. The
 * client is no worse off than before this component existed, and nothing here
 * ever renders an error where an answer about their funders belongs — an error
 * banner in that slot reads as "none", which is the one thing this whole
 * feature must not say (see CoFunders).
 *
 * ## Skipping costs nothing, and there is no skip button
 *
 * Same rule as `EinPrompt`: there is nothing to skip, because the page already
 * works without an EIN. "No, that's not us" and "None of these" fall through to
 * the optional field; ignoring the card entirely is a valid way to use the
 * product. The EIN is not a matching input, so a blank one costs no leads.
 *
 * The human still confirms — always. Nothing here writes `org_ein` without a
 * click, and an ambiguous name opens a chooser with nothing preselected. That
 * rule belongs to OrgIdentityConfirm and is not re-implemented here.
 *
 * ## Two modes, and the difference is what a wrong answer costs
 *
 * * **`fill`** — no EIN on file. A wrong confirmation writes a wrong value over
 *   a blank. Falls back to the typed field (`EinPrompt`) whenever the resolver
 *   cannot answer, because the client is otherwise left with nothing.
 * * **`correct`** — an EIN IS on file and no filing we hold names it. A wrong
 *   confirmation OVERWRITES a value the client may have got right, which is the
 *   worse failure, so: the stored EIN is shown next to the suggestion, "mine is
 *   right" is a first-class answer, and there is **no typed fallback** — when
 *   the resolver has nothing to offer this renders NOTHING and the panel's own
 *   copy (which already points at Preferences) stands unchanged. Adding an
 *   empty prompt there would imply their EIN is wrong on no evidence.
 *
 * ⚠️ In `correct` mode a suggestion is **by construction** a different EIN from
 * the stored one — if the stored EIN appeared in any filing we hold, the caller
 * would be `in_graph: true` and would never render this. That is what makes the
 * offer honest rather than a guess, and it is why the copy claims only that the
 * stored EIN appears in no filing, never that it is wrong.
 */
export default function OrgIdentitySetup({
  orgName,
  state,
  onSaved,
  mode = 'fill',
  currentEin = null,
}) {
  // 'loading' | 'match' | 'choose' | 'fallback'
  const [phase, setPhase] = useState('loading')
  const [suggestions, setSuggestions] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // The resolver needs both, and `state` is nullable on the profile.
  const canLookup = Boolean(orgName && orgName.trim().length >= 2 && state)

  useEffect(() => {
    if (!canLookup) {
      setPhase('fallback')
      return
    }
    let cancelled = false
    lookupOrg(orgName, state)
      .then(res => {
        if (cancelled) return
        const found = res.suggestions || []
        // 'none' — and anything unexpected — is the typed field, unchanged.
        if ((res.outcome === 'match' || res.outcome === 'choose') && found.length) {
          setSuggestions(found)
          setPhase(res.outcome)
        } else {
          setPhase('fallback')
        }
      })
      .catch(() => {
        // Includes the 403 an unconfirmed client gets. Deliberately silent:
        // "confirm your email to look up your EIN" is a worse thing to say in
        // this slot than simply showing the field that has always been there.
        if (!cancelled) setPhase('fallback')
      })
    return () => { cancelled = true }
  }, [orgName, state, canLookup])

  async function confirm(org) {
    if (saving) return
    setError('')
    setSaving(true)
    try {
      await updatePreferences({ org_ein: org.ein })
      // Not clearing `saving` first: the button stays busy until the answer it
      // promised has replaced this card.
      await onSaved?.()
    } catch (err) {
      // Fall through to the typed field rather than stranding the reader on a
      // card whose only button just failed — they can still enter it by hand.
      setError(err.message || 'Could not save that EIN.')
      setPhase('fallback')
      setSaving(false)
    }
  }

  if (phase === 'fallback') {
    // Correction mode has no typed fallback — see the mode note above. An error
    // still shows, because a failed write the client attempted is theirs to know
    // about; silence there would look like it worked.
    if (mode === 'correct') {
      return error ? (
        <p className="text-sm text-red-600 mt-3" aria-live="polite">{error}</p>
      ) : null
    }
    return (
      <>
        {error && (
          <p className="text-sm text-red-600" aria-live="polite">{error}</p>
        )}
        <EinPrompt onSaved={onSaved} cta="Show me" />
      </>
    )
  }

  return (
    <div className="mt-4 max-w-md mx-auto text-left">
      <OrgIdentityConfirm
        state={phase}
        suggestions={suggestions}
        busy={saving}
        currentEin={mode === 'correct' ? currentEin : null}
        onConfirm={confirm}
        onReject={() => setPhase('fallback')}
      />
    </div>
  )
}
